import { useTranslation } from 'react-i18next';
import { PanelHead } from './panel';
import { metricText, type Fmt } from './format';
import type { PeriodReport } from './use-report';

const RANK_KEYS = ['1', '2', '3', '4', '5', '6', '7'] as const;

/** The seven sales levels as a schematic funnel: dropped on the left, level and count inside, conversion and cost on the right. */
export function Funnel({ report, fmt }: { report: PeriodReport; fmt: Fmt }) {
  const { t } = useTranslation(['performance', 'common']);
  const steps = report.funnel;
  const first = steps[0]?.reached ?? 0;
  const last = steps[6]?.reached ?? 0;
  return (
    <article className="pa-panel" id="pa-funnel">
      <PanelHead title={t('dashboard.funnel.title')} subtitle={t('dashboard.funnel.subtitle')}>
        <div className="pa-funnel-summary">
          <div>
            <strong>{first > 0 ? fmt.pct(last / first) : '—'}</strong>
            <span>{t('dashboard.funnel.leadToReservation')}</span>
          </div>
          <div>
            <strong>{fmt.int(report.totals.noContact)}</strong>
            <span>{t('dashboard.funnel.noContact')}</span>
          </div>
        </div>
      </PanelHead>
      <div className="pa-funnel-scroll" tabIndex={0} aria-label={t('dashboard.funnel.aria')}>
        <div className="pa-funnel-columns" aria-hidden="true">
          <span>{t('dashboard.funnel.colLoss')}</span>
          <span>{t('dashboard.funnel.colStage')}</span>
          <span>{t('dashboard.funnel.colResult')}</span>
        </div>
        <ol className="pa-funnel-list">
          {steps.map((s, i) => (
            <li key={s.rank} className="pa-funnel-row">
              <div className="pa-funnel-loss">
                <strong>{i === 6 ? '—' : s.lost ? `−${fmt.int(s.lost)}` : '0'}</strong>
                <small>
                  {i === 6
                    ? t('dashboard.funnel.final')
                    : i === 0
                      ? t('dashboard.funnel.lossNoContact')
                      : t('dashboard.funnel.lossLevel')}
                </small>
              </div>
              <div
                className="pa-funnel-shape"
                style={{
                  ['--stage-width' as string]: `${100 - i * 8}%`,
                  ['--stage-color' as string]: `var(--hp-funnel-stage-${i})`,
                }}
              >
                <svg viewBox="0 0 1000 60" preserveAspectRatio="none" aria-hidden="true" focusable="false">
                  <polygon
                    points="10,4 990,4 945,56 55,56"
                    fill="currentColor"
                    stroke="currentColor"
                    strokeWidth="8"
                    strokeLinejoin="round"
                    vectorEffect="non-scaling-stroke"
                  />
                </svg>
                <div>
                  <span>{t(`rank.${RANK_KEYS[i] ?? '1'}`)}</span>
                  <strong>{fmt.int(s.reached)}</strong>
                </div>
              </div>
              <div className="pa-funnel-result">
                <strong>{i === 0 ? (first > 0 ? '100 %' : '—') : metricText(s.conversion, (v) => fmt.pct(v))}</strong>
                <small>{i === 0 ? t('dashboard.funnel.entry') : t('dashboard.funnel.ofPrevious')}</small>
                <span>
                  {metricText(s.costPer, (v) => fmt.czk(v))} {t('dashboard.funnel.perResult')}
                </span>
              </div>
            </li>
          ))}
        </ol>
      </div>
      <p className="pa-funnel-caption">{t('dashboard.funnel.caption')}</p>
    </article>
  );
}
